import { useCallback, useEffect, useState } from 'react';

import client from '../api/client';

// Soft-deleted orders (/api/admin, requireAdmin). Deleted orders never
// appear in useAdminOrders' listing — this is the only way to find one to
// restore.
// GET /admin/orders/deleted?status=&page=&limit= -> { items, page, limit, total, pages }.
// POST /admin/orders/:id/restore -> restored order; 400 if it wasn't deleted.
const EMPTY = { items: [], page: 1, limit: 20, total: 0, pages: 1 };

export default function useAdminDeletedOrders({ status = '', page = 1, limit = 20 } = {}) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const buildParams = useCallback(() => {
    const params = { page, limit };
    if (status) params.status = status;
    return params;
  }, [status, page, limit]);

  const reload = useCallback(async () => {
    const { data: res } = await client.get('/admin/orders/deleted', { params: buildParams() });
    setData(res && Array.isArray(res.items) ? res : EMPTY);
  }, [buildParams]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    client
      .get('/admin/orders/deleted', { params: buildParams() })
      .then(({ data: res }) => {
        if (active) setData(res && Array.isArray(res.items) ? res : EMPTY);
      })
      .catch((err) => {
        if (active) {
          setData(EMPTY);
          setError(err);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [buildParams]);

  const restore = useCallback(
    async (id) => {
      const { data: res } = await client.post(`/admin/orders/${id}/restore`);
      await reload();
      return res;
    },
    [reload]
  );

  return { ...data, loading, error, reload, restore };
}
